import type { LocationData } from '../../components/addressInput';

export interface StayAddress {
  street: string;
  unit: string;
  city: string;
  state: string;
  zipCode: string;
  latitude: number | null;
  longitude: number | null;
}

export const EMPTY_ADDRESS: StayAddress = {
  street: '',
  unit: '',
  city: '',
  state: '',
  zipCode: '',
  latitude: null,
  longitude: null,
};

/** What is missing before the address can be saved; null when it is complete. */
export function addressProblem(address: StayAddress, country: string = 'US'): string | null {
  // Spec 028: a Canadian brand asks for the province and the postal code.
  const canada = country === 'CA';
  if (address.latitude === null || address.longitude === null) {
    return 'Search for the address and pick it from the list';
  }
  if (!address.street.trim() || !address.city.trim() || !address.state.trim()) {
    return canada ? 'Fill in the street, city and province' : 'Fill in the street, city and state';
  }
  if (!address.zipCode.trim()) return canada ? 'Fill in the postal code' : 'Fill in the ZIP code';
  return null;
}

/** The `StayAddressInput` the API takes. */
export function toAddressInput(address: StayAddress) {
  return {
    street: address.street.trim(),
    unit: address.unit.trim() || null,
    city: address.city.trim(),
    state: address.state.trim(),
    zipCode: address.zipCode.trim(),
    latitude: address.latitude as number,
    longitude: address.longitude as number,
  };
}

/** Street from a Places result: the part of the formatted address before the city. */
export function addressFromPlace(place: LocationData, previous: StayAddress): StayAddress {
  const street = (place.address || place.formattedAddress || '').split(',')[0]?.trim() ?? '';
  return {
    ...previous,
    street: street || previous.street,
    city: place.city ?? previous.city,
    state: place.state ?? previous.state,
    zipCode: place.zipCode ?? previous.zipCode,
    latitude: place.latitude,
    longitude: place.longitude,
  };
}
